import type { SxProps, Theme } from "@mui/material"

/**
 * Merges multiple `sx` props into a single one.
 *
 * MUI's `sx` prop can be an object, a theme callback `(theme) => ({...})` or an
 * array combining both, but there is no built-in helper to combine several `sx`
 * values (e.g. a component's default styles + styles passed by the caller).
 * This utility flattens every input into the array form that MUI can resolve,
 * preserving order (later entries win) and keeping theme callbacks intact.
 *
 * It is "safe" because it accepts objects, functions, and arrays of objects and functions
 *
 * It can accept `false` and `undefined`: this lets callers
 * pass conditional styles inline (`cond && sx`, optional props) without extra
 * guards. Those falsy values are simply kept in the array, where MUI ignores
 * them at resolution time.
 *
 * safeMergeSx(baseSx, isActive && activeSx, props.sx)
 */
export const safeMergeSx = (
  ...sxs: (SxProps<Theme> | false | undefined)[]
): SxProps<Theme> => sxs.flatMap((sx) => (Array.isArray(sx) ? sx : [sx]))
